<template>
  <div class="new-dispatch-modal">
    <div class="form-grid">
      <div class="form-group">
        <label for="datePicker">Dispatch Date<span class="required">*</span></label>
        <input 
          type="date" 
          v-model="formData.date" 
          id="datePicker" 
          required 
          class="search-input styled-input"
          :disabled="hasSelectedAllocations"
        >
      </div>

      <div class="form-group">
        <label for="vehicle">Vehicle <span class="required">*</span></label>
        <select 
          v-model="formData.salesRep"
          id="vehicle"
          required
          :disabled="hasSelectedAllocations"
        >
          <option disabled value="">Select Vehicle</option>
          <option v-for="rep in salesReps" :key="rep.profile_id" :value="rep.profile_id">{{ rep.name }}</option>
        </select>
        <span v-if="errors.salesRep" class="error-message">Please select a Vehicle</span>
      </div>
    </div>

    <div>
      <h3 class="text-center mt-3">Stock Allocation Items</h3>
      <table class="table table-responsive-sm">
        <thead>
        <tr>
          <th>Select</th>
          <th>Stock Allocation ID</th>
          <th>Allocation Date</th>

          <th>Business Name</th>
          <th>Item</th>
          <th>Qty</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="allocation in selectedRepStockAllocations" :key="allocation.id">
          <td>
            <input type="checkbox" v-model="selectedAllocations" :value="allocation.id">
          </td>
          <td>{{ allocation.stock_allocations_no }}</td>
          <td>{{ allocation.stock_allocations_date }}</td>
          <td>{{ allocation.business_name }}</td>
          <td>
            <ul>
              <li v-for="item in allocation.items" :key="item.item_id">{{ item.item_name }}</li>
            </ul>
          </td>
          <td>
            <ul>
              <li v-for="item in allocation.items" :key="item.item_id">{{ item.item_qty }}</li>
            </ul>
          </td>
        </tr>
        </tbody>
      </table>
      <span v-if="errors.allocations" class="error-message">Please select at least one Stock Allocation</span>
    </div>

    <div class="button-group mt-5">
      <button type="button" class="btn btn-danger" @click="UpdateDispatch" :disabled="submitLoading || loading">
        <i v-if="submitLoading" class="fa fa-spinner fa-spin mr-2"></i>
        {{ submitLoading ? 'Updating...' : 'Update' }}
      </button>
    </div>
  </div>
</template>

<script>
import {
  fetchSalesReps,
  dispatchupdateitems,
  updatedispatchdetails,
  dispatcheditstock
} from '@/services/dataService';
import { useToast } from "vue-toastification";

export default {
  name: "EditDispatch",
  data() {
    return {
      formData: {
        salesRep: "",
        date: ""
      },
      salesReps: [],
      selectedRepStockAllocations: [],
      selectedAllocations: [],
      profile_id: null,
      user_id: null,
      company_id: null,
      dispatchId: null,
      reporting_profile_id: null,
      user_category: null,
      errors: {
        salesRep: false,
        allocations: false,
      },
      loading: false,
      submitLoading: false,
      token: localStorage.getItem('token_set') || ''
    };
  },

  setup() {
    const toast = useToast();
    return { toast };
  },

  computed: {
    hasSelectedAllocations() {
      return this.selectedAllocations.length > 0;
    }
  },

  async mounted() {
    try {
      this.profile_id = localStorage.getItem('profile_id');
      this.reporting_profile_id = localStorage.getItem('reportingL1ProfileId');
      this.user_category = localStorage.getItem('user_label');
      this.user_id = localStorage.getItem('user_id');
      this.company_id = localStorage.getItem('company_id');
      this.dispatchId = this.$route.params.id; // Capture dispatchId from the route

      await this.loginAndFetchData();

      // Fetch and populate dispatch data for update if dispatchId exists
      if (this.dispatchId) {
        await this.fetchDispatchDetails(this.dispatchId);
      }
    } catch (error) {
      console.error('Error during mounted:', error);
    }
  },

  methods: {
    async loginAndFetchData() {
      try {
        await this.fetchDispatchData();

        if (this.formData.salesRep) {
          await this.fetchRepStockAllocations(this.formData.salesRep);
        }
      } catch (error) {
        console.error('Login error:', error);
      }
    },

    async fetchDispatchDetails(dispatchId) {
      try {
        this.loading = true;
        const response = await dispatchupdateitems(dispatchId, this.token);
        const dispatchData = response.data.data.dispatch;
        const stockAllocations = response.data.data.stock_allocations;

        this.formData.salesRep = dispatchData.rep_id;
        this.formData.date = dispatchData.sales_dispatches_date;
        this.selectedAllocations = stockAllocations;

        await this.fetchRepStockAllocations();
      } catch (error) {
        console.error('Error fetching dispatch details:', error);
      } finally {
        this.loading = false;
      }
    },

    async fetchDispatchData() {
      try {
        const profileId = this.profile_id;
        const token = this.token;
        const salesRepsData = await fetchSalesReps(profileId, profileId, token);
        this.salesReps = salesRepsData?.data?.data || [];
      } catch (error) {
        console.error("Error fetching dispatch data:", error);
      }
    },

    async fetchRepStockAllocations() {
      try {
        const token = this.token;
        const profileId = this.formData.salesRep;
        const payload = {
          stock_allocations: this.selectedAllocations,
        };

        const repStockAllocationsData = await dispatcheditstock(profileId, payload, token);

        if (repStockAllocationsData && repStockAllocationsData.data) {
          this.selectedRepStockAllocations = repStockAllocationsData.data.data;
        } else {
          console.error("No data found for rep stock allocations.");
        }
      } catch (error) {
        console.error("Error fetching rep stock allocations:", error);
      }
    },
    async UpdateDispatch() {
      if (!this.validateForm()) {
        console.error("Validation failed");
        this.toast.error("Please fill out all required fields");
        return;
      }

      try {
        this.submitLoading = true;

        const activeProfileId = this.user_category === 'DISTRIBUTOR BACK OFFICE' ? this.reporting_profile_id : this.profile_id;
        const payload = {
          created_by: activeProfileId,
          company_id: this.company_id,
          checked_by: activeProfileId,
          rep_id: this.formData.salesRep,
          stock_allocations_no: this.selectedAllocations,
          sales_dispatches_date: this.formData.date
        };

        const response = await updatedispatchdetails(this.dispatchId, payload, this.token);

        if (response.data.status === "success") {
          this.toast.success(response.data.message);
          await this.$router.push('/sales/dispatch');
        } else if (response.data.status === "failed") {
          this.toast.error(response.data.message);
        }
      } catch (error) {
        console.error("Error updating dispatch:", error);
        this.toast.error("Failed to update dispatch.");
      } finally {
        this.submitLoading = false;
      }
    },
    validateForm() {
      this.errors.salesRep = !this.formData.salesRep;
      this.errors.allocations = this.selectedAllocations.length === 0;
      return !Object.values(this.errors).some(error => error);
    }
  }
};
</script>

<style scoped>

.new-dispatch-modal {
  padding: 20px;
  background-color: #fff;
  border-radius: 10px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  position: relative;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

h2 {
  color: #1b2a50;
  font-size: 24px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  width: 100%;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 15px;
}

label {
  font-weight: bold;
  margin-bottom: 8px;
  color: #1b2a50;
  text-align: left;
}

.required {
  color: red;
}

select {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
  width: 100%;
}

.button-group {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

ul {
  list-style-type: none;
  padding: 0;
}

li {
  margin-bottom: 10px;
}

input[type="checkbox"] {
  margin-right: 10px;
}

.error-message {
  color: red;
  font-size: 14px;
  margin-top: 5px;
}

.styled-input {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
  width: 100%;
}
</style>